import { useEffect, useState } from 'react';
import './ProgramPages.css';
import { IconBook, IconCalculator, IconCheck, IconGlobe, IconSparkles } from './ProgramIcons';
import heroImage1 from './assets/news/news4.jpg';
import heroImage2 from './assets/news/news5.jpg';
import heroImage3 from './assets/news/news6.jpg';

function KindergartenPage({ onNavigate }) {
  const [activeHero, setActiveHero] = useState(0);
  const heroSlides = [heroImage1, heroImage2, heroImage3];

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveHero((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  const programHighlights = [
    {
      icon: IconBook,
      title: 'Literacy Foundation',
      description: 'Phonics, reading readiness, and writing skills through engaging stories and activities.',
    },
    {
      icon: IconCalculator,
      title: 'Math Readiness',
      description: 'Number sense, counting, basic operations, and problem-solving through hands-on learning.',
    },
    {
      icon: IconGlobe,
      title: 'World Exploration',
      description: 'Science, social studies, and cultural awareness that spark curiosity about the world.',
    },
    {
      icon: IconSparkles,
      title: 'Creative Expression',
      description: 'Arts, music, drama, and movement that build confidence and imagination.',
    },
  ];

  const readinessSkills = [
    {
      category: 'Academic Readiness',
      skills: [
        'Recognizes letters and sounds',
        'Counts to 20 and beyond',
        'Writes own name',
        'Follows multi-step directions',
        'Asks questions and seeks answers',
      ],
    },
    {
      category: 'Social-Emotional Readiness',
      skills: [
        'Shares and takes turns',
        'Manages emotions appropriately',
        'Works cooperatively in groups',
        'Shows empathy and kindness',
        'Resolves conflicts peacefully',
      ],
    },
    {
      category: 'Physical Readiness',
      skills: [
        'Uses scissors and writing tools',
        'Participates in physical activities',
        'Demonstrates coordination',
        'Practices self-care routines',
        'Follows safety rules',
      ],
    },
  ];

  const weeklyThemes = [
    {
      week: 'Week 1-2',
      theme: 'All About Me',
      focus: 'Identity, family, feelings, and building classroom community',
    },
    {
      week: 'Week 3-4',
      theme: 'Our Community',
      focus: 'Helpers, places, rules, and working together',
    },
    {
      week: 'Week 5-6',
      theme: 'Nature & Seasons',
      focus: 'Weather, plants, animals, and environmental care',
    },
    {
      week: 'Week 7-8',
      theme: 'Stories & Imagination',
      focus: 'Fairy tales, creative writing, and dramatic play',
    },
  ];

  const learningApproach = [
    {
      title: 'Balanced Curriculum',
      description: 'Structured lessons combined with play-based exploration for optimal learning.',
      image: heroImage1,
    },
    {
      title: 'Small Group Instruction',
      description: 'Targeted teaching in literacy and math groups based on individual needs.',
      image: heroImage2,
    },
    {
      title: 'Project-Based Learning',
      description: 'Hands-on projects that integrate multiple subjects and real-world connections.',
      image: heroImage3,
    },
    {
      title: 'Character Development',
      description: 'Daily lessons in values, responsibility, and faith-based character building.',
      image: heroImage1,
    },
  ];

  return (
    <div className="program-page kindergarten-page" id="kindergarten">
      {/* Hero Section */}
      <section className="program-hero">
        <div className="program-hero-media">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`program-hero-slide ${index === activeHero ? 'active' : ''}`}
            >
              <img src={slide} alt="Kindergarten" />
            </div>
          ))}
        </div>
        <div className="program-hero-overlay" />
        <div className="program-hero-content">
          <span className="program-hero-badge">Ages 5-6</span>
          <h1>Kindergarten Program</h1>
          <p className="program-hero-subtitle">
            Building strong foundations in literacy, numeracy, and character for primary success
          </p>
          <div className="program-hero-stats">
            <div className="program-stat">
              <span className="stat-number">18</span>
              <span className="stat-label">Max Class Size</span>
            </div>
            <div className="program-stat">
              <span className="stat-number">2</span>
              <span className="stat-label">Teachers per Class</span>
            </div>
            <div className="program-stat">
              <span className="stat-number">6</span>
              <span className="stat-label">Hours Daily</span>
            </div>
          </div>
        </div>
      </section>

      {/* Program Overview */}
      <section className="program-overview">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Program Overview</span>
            <h2>Ready for Primary School</h2>
            <p>
              Our kindergarten program prepares children for primary school success through 
              structured learning, social development, and joyful discovery. We focus on 
              building confidence, curiosity, and essential academic skills.
            </p>
          </div>

          <div className="program-highlights-grid">
            {programHighlights.map((item, index) => {
              const Icon = item.icon;
              return (
              <div className="highlight-card" key={index}>
                <div className="highlight-icon" aria-hidden="true">
                  <Icon />
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Readiness Skills */}
      <section className="readiness-skills">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Learning Outcomes</span>
            <h2>Kindergarten Readiness Skills</h2>
            <p>By the end of kindergarten, students will demonstrate these key competencies</p>
          </div>

          <div className="readiness-grid">
            {readinessSkills.map((category, index) => (
              <div className="readiness-category" key={index}>
                <h3>{category.category}</h3>
                <ul>
                  {category.skills.map((skill, idx) => (
                    <li key={idx}>
                      <span className="check-icon" aria-hidden="true">
                        <IconCheck />
                      </span>
                      {skill}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Weekly Themes */}
      <section className="weekly-themes">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Curriculum</span>
            <h2>Sample Weekly Themes</h2>
            <p>Integrated learning through engaging themes that connect subjects</p>
          </div>

          <div className="themes-grid">
            {weeklyThemes.map((item, index) => (
              <div className="theme-card" key={index}>
                <div className="theme-week">{item.week}</div>
                <h4>{item.theme}</h4>
                <p>{item.focus}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Approach */}
      <section className="learning-areas">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Our Approach</span>
            <h2>How We Teach</h2>
            <p>Research-based methods that engage young learners</p>
          </div>

          <div className="learning-areas-grid">
            {learningApproach.map((area, index) => (
              <div className="learning-area-card" key={index}>
                <div className="learning-area-image">
                  <img src={area.image} alt={area.title} />
                </div>
                <div className="learning-area-content">
                  <h3>{area.title}</h3>
                  <p>{area.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="campus-link">
        <div className="program-container">
          <div className="campus-link-card">
            <div>
              <p className="campus-link-kicker">School Center</p>
              <h3>School center information</h3>
              <p>
                This program is part of our main campus network. Visit the central school site for
                official information and updates.
              </p>
            </div>
            <a
              className="campus-link-action"
              href="https://kinderfieldhighfield.sch.id/simprug"
              target="_blank"
              rel="noreferrer"
            >
              Open main campus site
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="parent-info">
        <div className="program-container">
          <div className="cta-section">
            <h3>Is Your Child Ready for Kindergarten?</h3>
            <p>Join us for an assessment and campus tour to learn more</p>
            <div className="cta-buttons">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => onNavigate && onNavigate('register')}
              >
                Apply Now
              </button>
              <a href="/contact" className="btn btn-secondary">Book Assessment</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default KindergartenPage;
